import { idbRequest, idbTransactionDone, openIdb } from '../Utils/idb';

const DB_NAME = 'mdv-hole-offline';
const DB_VERSION = 1;
const HOLES_STORE = 'holes';
const QUERIES_STORE = 'queries';
const FILTER_LS = 'mdv-hole-filter-cache';
const SCHEMA_LS = 'mdv-hole-schema-cache';

let dbPromise = null;

function openHoleDb() {
  if (!dbPromise) {
    dbPromise = openIdb(DB_NAME, DB_VERSION, (db) => {
      if (!db.objectStoreNames.contains(HOLES_STORE)) {
        db.createObjectStore(HOLES_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(QUERIES_STORE)) {
        db.createObjectStore(QUERIES_STORE, { keyPath: 'id' });
      }
    }).catch((err) => {
      dbPromise = null;
      throw err;
    });
  }
  return dbPromise;
}

export function holeQueryCacheId({ dataset, filters, isplan } = {}) {
  return JSON.stringify({
    dataset: dataset || '',
    filters: filters || null,
    isplan: !!isplan,
  });
}

export async function cacheHoleRecords(rows = []) {
  if (!rows.length) return;
  try {
    const db = await openHoleDb();
    const tx = db.transaction(HOLES_STORE, 'readwrite');
    const store = tx.objectStore(HOLES_STORE);
    rows.forEach((row) => {
      if (row?.id) store.put(row);
    });
    await idbTransactionDone(tx);
  } catch (err) {
    console.warn('[hole cache] could not save holes', err?.message || err);
  }
}

export async function readAllCachedHoleRecords() {
  try {
    const db = await openHoleDb();
    return (await idbRequest(db.transaction(HOLES_STORE, 'readonly').objectStore(HOLES_STORE).getAll())) || [];
  } catch (err) {
    console.warn('[hole cache] could not read holes', err?.message || err);
    return [];
  }
}

export async function cacheQueryKeys(id, keys = []) {
  if (!id || !keys.length) return;
  try {
    const db = await openHoleDb();
    const tx = db.transaction(QUERIES_STORE, 'readwrite');
    tx.objectStore(QUERIES_STORE).put({
      id,
      keys,
      savedAt: Date.now(),
    });
    await idbTransactionDone(tx);
  } catch (err) {
    console.warn('[hole cache] could not save query keys', err?.message || err);
  }
}

export async function readCachedQueryKeys(id) {
  if (!id) return null;
  try {
    const db = await openHoleDb();
    const row = await idbRequest(db.transaction(QUERIES_STORE, 'readonly').objectStore(QUERIES_STORE).get(id));
    return Array.isArray(row?.keys) ? row.keys : null;
  } catch (err) {
    console.warn('[hole cache] could not read query keys', err?.message || err);
    return null;
  }
}

export function saveFilterSnapshot(projectName, { savedFilters = [], sharedFilters = [], schema = null } = {}) {
  const project = String(projectName || '').trim() || 'default';
  try {
    localStorage.setItem(
      FILTER_LS,
      JSON.stringify({
        project,
        savedAt: Date.now(),
        savedFilters,
        sharedFilters,
      })
    );
  } catch {
    // quota
  }
  if (schema) saveSchemaSnapshot(schema);
}

export function readFilterSnapshot(projectName) {
  try {
    const raw = localStorage.getItem(FILTER_LS);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return null;
    const project = String(projectName || '').trim() || 'default';
    if (data.project && data.project !== project) return data;
    return data;
  } catch {
    return null;
  }
}

export function saveSchemaSnapshot(schema) {
  if (!schema || typeof schema !== 'object') return;
  try {
    localStorage.setItem(SCHEMA_LS, JSON.stringify({ savedAt: Date.now(), schema }));
  } catch {
    // quota
  }
}

export function readSchemaSnapshot() {
  try {
    const raw = localStorage.getItem(SCHEMA_LS);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data?.schema && typeof data.schema === 'object' ? data.schema : null;
  } catch {
    return null;
  }
}
